import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, hasProfile } from '../Services/api'
import { track } from '../Utils/analytics'
import {
  ACROSS,
  cellKey,
  emptyProgress,
  entryRows,
  isSolvedBy,
  loadProgress,
  other,
  saveProgress,
  toLetter,
  wordAt,
} from '../Utils/crossword'

const firstCell = (word, entries) =>
  word.cells.find(([r, c]) => !entries[cellKey(r, c)]) || word.cells[0]

/* One puzzle being played: the letters, the selected square and direction,
   the timer (it starts with the first letter and stops while the tab is
   hidden), "Provjeri", revealing, and the solve sent to the server.
   Progress is saved in this browser after every change (Utils/crossword.js). */
export default function useCrossword(puzzle, { onSolved } = {}) {
  const day = puzzle.date
  const [progress, setProgress] = useState(() => loadProgress(day))
  const [sel, setSel] = useState(() => firstCell(puzzle.words[0], progress.entries))
  const [dir, setDir] = useState(puzzle.words[0].dir)
  const [checked, setChecked] = useState(false)
  const [result, setResult] = useState(null) // the server's answer to the solve
  const startSent = useRef(false)
  const progressRef = useRef(progress)
  progressRef.current = progress
  const solvedCallback = useRef(onSolved)
  solvedCallback.current = onSolved

  useEffect(() => saveProgress(day, progress), [day, progress])

  // The timer: one second at a time while visible, started and not solved.
  useEffect(() => {
    if (!progress.started || progress.solved) return undefined
    let timer = null
    const tick = () => setProgress((p) => (p.solved ? p : { ...p, secs: p.secs + 1 }))
    const run = () => {
      clearInterval(timer)
      timer = document.hidden ? null : setInterval(tick, 1000)
    }
    run()
    document.addEventListener('visibilitychange', run)
    return () => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', run)
    }
  }, [progress.started, progress.solved])

  const submit = useCallback(
    (p, { quiet = false } = {}) => {
      api
        .crosswordSolve(day, {
          grid: entryRows(puzzle, p.entries),
          seconds: p.secs,
          revealed: Object.keys(p.revealed).length,
        })
        .then((data) => {
          setProgress((cur) => ({ ...cur, submitted: true }))
          setResult(data)
          if (!quiet) solvedCallback.current?.(data)
        })
        .catch(() => {
          // Offline or the server is down: the next visit sends it again.
          setResult({ error: true })
          if (!quiet) solvedCallback.current?.({ error: true })
        })
    },
    [day, puzzle]
  )

  // On opening: send a solve that didn't get through; take a result this
  // profile already has (solved here before the browser's progress was lost).
  useEffect(() => {
    if (progress.solved && !progress.submitted) {
      submit(progress, { quiet: true })
      return
    }
    if (progress.solved || !hasProfile()) return
    let alive = true
    api
      .crosswordResult(day)
      .then((r) => {
        if (!alive || !r?.solved) return
        const entries = {}
        puzzle.whites.forEach(([y, x]) => {
          entries[cellKey(y, x)] = puzzle.solution(y, x)
        })
        setProgress({
          ...emptyProgress(),
          entries,
          secs: r.seconds || 0,
          started: true,
          solved: true,
          submitted: true,
        })
        setResult(r)
      })
      .catch(() => {})
    return () => {
      alive = false
    }
    // Only when a puzzle opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day])

  const [sr, sc] = sel
  let curDir = dir
  let current = wordAt(puzzle, sr, sc, curDir)
  if (!current) {
    curDir = other(dir)
    current = wordAt(puzzle, sr, sc, curDir)
  }

  /* New letters (and reveals): saves them, starts the timer with the first
     one, and notices the solve. */
  const put = useCallback(
    (entries, revealed) => {
      const p = progressRef.current
      if (p.solved) return
      const next = { ...p, entries, revealed: revealed || p.revealed }
      if (!p.started) {
        next.started = true
        if (!startSent.current) {
          startSent.current = true
          api.crosswordStart(day).catch(() => {})
          track('crossword_start', { date: day })
        }
      }
      const solved = isSolvedBy(puzzle, entries)
      if (solved) next.solved = true
      progressRef.current = next
      setProgress(next)
      setChecked(false)
      if (solved) {
        track('crossword_solve', {
          date: day,
          seconds: next.secs,
          helped: Object.keys(next.revealed).length > 0,
        })
        submit(next)
      }
    },
    [day, puzzle, submit]
  )

  const select = useCallback(
    (r, c) => {
      if (r === sr && c === sc) {
        if (wordAt(puzzle, r, c, other(curDir))) setDir(other(curDir))
        return
      }
      setSel([r, c])
      setDir(wordAt(puzzle, r, c, curDir) ? curDir : other(curDir))
    },
    [puzzle, sr, sc, curDir]
  )

  const goWord = useCallback(
    (step) => {
      const i = puzzle.words.indexOf(current)
      const next = puzzle.words[(i + step + puzzle.words.length) % puzzle.words.length]
      setSel(firstCell(next, progress.entries))
      setDir(next.dir)
    },
    [puzzle, current, progress.entries]
  )

  const pickWord = useCallback(
    (word) => {
      setSel(firstCell(word, progress.entries))
      setDir(word.dir)
    },
    [progress.entries]
  )

  const type = useCallback(
    (letter) => {
      if (progress.solved || !current) return
      const k = cellKey(sr, sc)
      const entries = progress.revealed[k] ? progress.entries : { ...progress.entries, [k]: letter }
      // Next: the next empty square of this word, else the next square, else
      // the next word's first empty square.
      const i = current.cells.findIndex(([y, x]) => y === sr && x === sc)
      const after = current.cells.slice(i + 1)
      const target = after.find(([y, x]) => !entries[cellKey(y, x)]) || after[0]
      if (target) {
        setSel(target)
      } else {
        const wi = puzzle.words.indexOf(current)
        const next = puzzle.words[(wi + 1) % puzzle.words.length]
        setSel(firstCell(next, entries))
        setDir(next.dir)
      }
      put(entries)
    },
    [progress, current, sr, sc, puzzle, put]
  )

  const back = useCallback(() => {
    if (progress.solved || !current) return
    const k = cellKey(sr, sc)
    const entries = { ...progress.entries }
    if (entries[k] && !progress.revealed[k]) {
      delete entries[k]
      put(entries)
      return
    }
    const i = current.cells.findIndex(([y, x]) => y === sr && x === sc)
    if (i > 0) {
      const [y, x] = current.cells[i - 1]
      if (!progress.revealed[cellKey(y, x)]) delete entries[cellKey(y, x)]
      setSel([y, x])
      put(entries)
    }
  }, [progress, current, sr, sc, put])

  const moveBy = useCallback(
    (dr, dc) => {
      let r = sr + dr
      let c = sc + dc
      while (r >= 0 && c >= 0 && r < puzzle.rows && c < puzzle.cols && puzzle.isBlack(r, c)) {
        r += dr
        c += dc
      }
      if (r < 0 || c < 0 || r >= puzzle.rows || c >= puzzle.cols) return
      const want = dr ? other(ACROSS) : ACROSS
      setSel([r, c])
      setDir(wordAt(puzzle, r, c, want) ? want : other(want))
    },
    [puzzle, sr, sc]
  )

  const toggleDir = useCallback(() => {
    if (wordAt(puzzle, sr, sc, other(curDir))) setDir(other(curDir))
  }, [puzzle, sr, sc, curDir])

  const reveal = useCallback(
    (cells) => {
      if (progress.solved) return
      const entries = { ...progress.entries }
      const revealed = { ...progress.revealed }
      cells.forEach(([y, x]) => {
        const k = cellKey(y, x)
        if (entries[k] !== puzzle.solution(y, x)) {
          entries[k] = puzzle.solution(y, x)
          revealed[k] = true
        }
      })
      track('crossword_reveal', { date: day, squares: cells.length })
      put(entries, revealed)
    },
    [progress, puzzle, day, put]
  )

  const restart = useCallback(() => {
    startSent.current = progress.submitted // a solved puzzle is already started on the server
    setProgress({ ...emptyProgress(), submitted: progress.submitted })
    setSel(firstCell(puzzle.words[0], {}))
    setDir(puzzle.words[0].dir)
    setChecked(false)
  }, [puzzle, progress.submitted])

  /* Physical keyboard: letters, Backspace, arrows, space (direction),
     Tab/Enter (next word, with Shift the one before). */
  const onKey = useCallback(
    (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return false
      const letter = e.key && e.key.length === 1 ? e.key : null
      const map = {
        Backspace: back,
        Delete: back,
        ArrowRight: () => moveBy(0, 1),
        ArrowLeft: () => moveBy(0, -1),
        ArrowDown: () => moveBy(1, 0),
        ArrowUp: () => moveBy(-1, 0),
        ' ': toggleDir,
        Tab: () => goWord(e.shiftKey ? -1 : 1),
        Enter: () => goWord(e.shiftKey ? -1 : 1),
      }
      if (map[e.key]) {
        e.preventDefault()
        map[e.key]()
        return true
      }
      const typed = toLetter(letter)
      if (typed) {
        e.preventDefault()
        type(typed)
        return true
      }
      return false
    },
    [back, moveBy, toggleDir, goWord, type]
  )

  const derived = useMemo(() => {
    let wrong = 0
    puzzle.whites.forEach(([r, c]) => {
      const letter = progress.entries[cellKey(r, c)]
      if (letter && letter !== puzzle.solution(r, c)) wrong += 1
    })
    const done = new Set(
      puzzle.words
        .filter((w) =>
          w.cells.every(([r, c]) => progress.entries[cellKey(r, c)] === puzzle.solution(r, c))
        )
        .map((w) => w.key)
    )
    return {
      wrong,
      filled: Object.keys(progress.entries).length,
      helped: Object.keys(progress.revealed).length > 0,
      done,
    }
  }, [puzzle, progress.entries, progress.revealed])

  return {
    progress,
    sel,
    dir: curDir,
    current,
    checked,
    result,
    setResult,
    ...derived,
    select,
    pickWord,
    type,
    back,
    goWord,
    toggleDir,
    onKey,
    check: () => setChecked(true),
    revealLetter: () => reveal([sel]),
    revealWord: () => current && reveal(current.cells),
    restart,
  }
}
